import { Controller } from "@hotwired/stimulus"

// Attached at runtime by ModalHelper#gl_modal (FOCUS_TRAP_CONTROLLER is merged into every
// gl_modal's data-controller list in app/helpers/modal_helper.rb); no template names it, by design.

// Shared gl_modal focus cycle (#13539, campaign #12564 — A11Y-2 dialog focus
// containment, MODAL-1 one emitter). Attached to EVERY gl_modal by the helper
// (app/helpers/modal_helper.rb merges it into data-controller); never declared
// by a template.
//
// Why Bootstrap's own FocusTrap is not enough: gl_modal renders at the end of
// <body>, so a Tab off the last focusable control finds no next focusable in
// the document and focus drops onto document.body. Bootstrap 5 refocuses only
// on a `focusin` whose target is outside the dialog — a drop to <body> fires no
// focusin, so nothing pulls it back (measured on 5 of 5 modals by the
// accessibility instrument, #13499). Shift+Tab off the first control is the
// mirror case. This controller wraps exactly those two keypresses and leaves
// every other Tab to the browser, so the order inside the dialog is still the
// DOM order the user sees.
//
// Bootstrap's `focus` option and the React dialog stack (FormModal's own trap
// in useDialogA11y / DetailPanel) are untouched.
const FOCUSABLE_SELECTOR = 'a[href], button, input:not([type="hidden"]), select, textarea, [tabindex]'
// Select2's OPEN dropdown is appended into the modal (dropdownParent) as its
// last child; Select2 closes it on Tab and returns focus to its combobox
// itself, so its search field is neither a stop nor "the last control".
const SELECT2_DROPDOWN_SELECTOR = ".select2-dropdown"
const SHOWN_MODAL_SELECTOR = ".modal.show"

// The order Bootstrap modals were SHOWN in, stamped from Bootstrap's own
// bubbling show.bs.modal / hidden.bs.modal events at the document level, so
// every Bootstrap modal counts — a gl_modal with this controller, a JS-built
// shell without it. Bootstrap never reorders or re-parents a modal element when
// it opens, so document order is not open order: a modal declared early in the
// layout and opened last is on top while sitting first in the DOM.
const shownOrder = new WeakMap()
let showSequence = 0
let trackingShowOrder = false

function trackShowOrder() {
  if (trackingShowOrder) return
  trackingShowOrder = true
  document.addEventListener("show.bs.modal", (event) => {
    if (event.target instanceof Element) shownOrder.set(event.target, ++showSequence)
  })
  document.addEventListener("hidden.bs.modal", (event) => {
    if (event.target instanceof Element) shownOrder.delete(event.target)
  })
}

export default class extends Controller {
  connect() {
    trackShowOrder()
    this.onKeydown = this.onKeydown.bind(this)
    this.element.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.onKeydown)
  }

  onKeydown(event) {
    if (event.key !== "Tab" || event.defaultPrevented) return
    if (event.ctrlKey || event.altKey || event.metaKey) return
    if (!this.topmostShownModal()) return
    if (event.target.closest(SELECT2_DROPDOWN_SELECTOR)) return

    const focusables = this.focusables()
    if (focusables.length === 0) return

    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement

    if (event.shiftKey && active === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  // The dialog on top of the stack owns the key: the shown modal with the
  // latest show.bs.modal stamp. Modals shown without Bootstrap's events (no
  // stamp) rank below stamped ones and among themselves by document order,
  // where a nested shown modal is a descendant (later) and an appended one is
  // later still. This controller acts only when the top is its own element.
  topmostShownModal() {
    const shown = Array.from(document.querySelectorAll(SHOWN_MODAL_SELECTOR))
    if (shown.length === 0) return true

    const orderOf = (el) => shownOrder.get(el) ?? -1
    const top = shown.reduce((current, el) => (orderOf(el) >= orderOf(current) ? el : current), shown[0])
    return top === this.element
  }

  // Enabled, painted, Tab-reachable descendants in DOM order — including
  // Select2's injected inline search textarea (a real stop) and excluding its
  // clipped tabindex=-1 original <select> and the open dropdown.
  focusables() {
    return Array.from(this.element.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => this.tabbable(el))
  }

  tabbable(el) {
    if (el.disabled || el.closest("[disabled]")) return false
    if (el.getAttribute("tabindex") === "-1") return false
    if (el.closest(SELECT2_DROPDOWN_SELECTOR)) return false
    return this.painted(el)
  }

  // A control the user can see. In a laid-out document a painted element has
  // a client rect (display:none on any ancestor removes it); jsdom performs no
  // layout and reports no rects for anything, so there the computed `display`
  // is read up the tree instead.
  painted(el) {
    if (el.closest("[hidden]")) return false
    if (getComputedStyle(el).visibility === "hidden") return false
    if (documentHasLayout()) return el.getClientRects().length > 0

    for (let node = el; node && node !== document.body; node = node.parentElement) {
      if (getComputedStyle(node).display === "none") return false
    }
    return true
  }
}

function documentHasLayout() {
  return document.documentElement.getClientRects().length > 0
}
